import React, { useState, useEffect } from 'react';

const fieldInputClass =
  'block w-full rounded-md border border-bsGray-300 bg-white px-3 py-1.5 text-base focus:outline-none focus:border-primaryColor/50 focus:ring-4 focus:ring-primaryColor/25 disabled:bg-bsGray-200';

// This component simulates more realistic crash scenarios
const CrashSimulator = () => {
  const [loading, setLoading] = useState(false);
  const [crashType, setCrashType] = useState('render');
  const [crashDelay, setCrashDelay] = useState(1000);
  const [crashMessage, setCrashMessage] = useState('Simulated data fetching error');

  // Simulated data fetching with potential crash
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        // Simulate different types of crashes
        switch (crashType) {
          case 'fetch':
            setLoading(false);
            throw new Error(`Fetch: ${crashMessage}`);

          case 'promise':
            // Use a separate function to throw the rejected promise
            // to avoid React's error boundary catching it directly
            const triggerPromiseRejection = () => {
              Promise.reject(new Error(`Promise: ${crashMessage}`));
            };
            triggerPromiseRejection();
            setLoading(false);
            break;

          case 'async':
            // Throw in next tick to avoid React error boundaries
            setTimeout(() => {
              throw new Error(`Async: ${crashMessage}`);
            }, 0);
            setLoading(false);
            break;

          case 'render':
            // We will handle this in the render method
            break;

          default:
            setLoading(false);
        }
      }, crashDelay);

      return () => clearTimeout(timer);
    }
  }, [loading, crashType, crashDelay, crashMessage]);

  // This will crash during render if loading is true and crashType is render
  if (loading && crashType === 'render') {
    // This will cause a render error - more specific message
    throw new Error(`Render crash: ${crashMessage}`);
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
  };

  return (
    <div className="mb-6 shadow-sm rounded-lg border border-black/10 bg-white">
      <div className="border-b border-black/10 px-4 py-2 bg-dangerColor text-white rounded-t-lg">
        Advanced Crash Testing
      </div>
      <div className="p-4">
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="mb-2 inline-block">Crash Type</label>
            <select
              value={crashType}
              onChange={(e) => setCrashType(e.target.value)}
              className={fieldInputClass}
            >
              <option value="render">Render Error</option>
              <option value="fetch">Data Fetch Error</option>
              <option value="promise">Promise Rejection</option>
              <option value="async">Async Function Error</option>
            </select>
            <div className="text-secondaryColor text-sm mt-1">
              Each crash type triggers a different error pathway.
            </div>
          </div>

          <div className="mb-4">
            <label className="mb-2 inline-block">Delay before crash (ms)</label>
            <input
              type="number"
              value={crashDelay}
              onChange={(e) => setCrashDelay(parseInt(e.target.value))}
              min="0"
              max="10000"
              className={fieldInputClass}
            />
          </div>

          <div className="mb-4">
            <label className="mb-2 inline-block">Error Message</label>
            <input
              type="text"
              value={crashMessage}
              onChange={(e) => setCrashMessage(e.target.value)}
              className={fieldInputClass}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-md border border-transparent px-3 py-1.5 text-base leading-normal text-center align-middle cursor-pointer select-none transition-colors duration-150 disabled:opacity-65 disabled:cursor-not-allowed bg-dangerColor text-white hover:bg-[#c44033]"
          >
            {loading ? 'Simulating...' : 'Simulate Crash'}
          </button>
        </form>
      </div>
      <div className="border-t border-black/10 px-4 py-2 text-secondaryColor rounded-b-lg">
        <small>This component simulates realistic crash scenarios to test error handling.</small>
      </div>
    </div>
  );
};

export default CrashSimulator;
